import { useState } from "react";
import { Plus, Minus } from "lucide-react";

/**
 * FAQ — the objections trade owners raise before signing up.
 *
 * Order = descending common-ness of the question in the target audience.
 * Copy is short, honest, and links to specific product areas where
 * applicable. No sales speak.
 */
const ITEMS = [
  {
    q: "Do I need to install anything?",
    a: "No. JobsBook is a web app — works on any modern browser. Staff access their day-view via a personal /s/slug URL with a 4-digit PIN, no app store install required.",
  },
  {
    q: "Can I keep using paper invoices for cash jobs?",
    a: "Yes. Pick 'Simple Cash' as the payment type and no invoice is generated. Those jobs stay out of the accounts ledger — they still show on the calendar so your staff know they're happening.",
  },
  {
    q: "Does it handle Irish VAT and RCT?",
    a: "Both. Default VAT rate 23% (editable per tenant), RCT at 0% (principal contractor status assumed). Invoices carry a booking code and are numbered sequentially per tenant.",
  },
  {
    q: "What if I only have 1 or 2 staff?",
    a: "Basic (€29/mo) is built for exactly this — solo operators and micro-teams. You can start there and upgrade to Pro when you hire.",
  },
  {
    q: "Can staff see each other's jobs?",
    a: "No. Each staff member sees only their own bookings in a rolling 4-day window. The manager sees everyone's jobs from the full calendar.",
  },
  {
    q: "What happens after the free trial?",
    a: "Nothing charges automatically. You'll be prompted to pick a plan when the trial ends. If you skip, your data is retained for 30 days so you can come back — after that it's deleted.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      className="bg-background border-t border-border scroll-mt-20"
      data-testid="landing-faq"
    >
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="text-center">
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">
            // Answers
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
            Questions we hear a lot.
          </h2>
        </div>

        <div className="mt-12 space-y-3">
          {ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                className={`border rounded-xl transition-all overflow-hidden ${
                  isOpen ? "border-accent bg-card shadow-md" : "border-border bg-card hover:border-accent/60"
                }`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-5 md:px-6 py-4 flex items-center justify-between gap-3"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-bold text-base md:text-lg">{item.q}</span>
                  <span className={`h-7 w-7 rounded-full grid place-items-center shrink-0 transition-colors ${isOpen ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>
                    {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 md:px-6 pb-5 -mt-1 text-muted-foreground leading-relaxed text-sm md:text-base">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
